import { FC, useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useSelectedSubscription } from "../context/subscription";
import { FlexDiv } from "components/Layout/FlexDiv";
import { OtherSettingsWrapper, ActiveSwitch } from "./styles";
import { OtherSettingsInputs } from "./model";
import { useConditionalSnackBar } from "context/snackbar";
import useEditOtherSettings from "hooks/services/subscription/useEditOtherSettings";
import { ChipInput } from "components/ChipInput";
import { useAuth } from "context/auth";

const OtherSettingsInfo: FC = () => {
  const { admin } = useAuth();
  const { subscription, isLoadingSubscription } = useSelectedSubscription();

  const [inputError, setInputError] = useState<string | null>(null);

  const [currency, setCurrency] = useState<string>("");
  const [monthScrapeRequest, setMonthScrapeRequest] = useState<number | null>(
    null
  );
  const [monthVerifyRequest, setMonthVerifyRequest] = useState<number | null>(
    null
  );
  const [monthFindPhoneRequest, setMonthFindPhoneRequest] = useState<
    number | null
  >(null);
  const [monthGenerateTemplateRequest, setMonthGenerateTemplateRequest] =
    useState<number | null>(null);
  const [hasLastFeatures, setHasLastFeatures] = useState<boolean>(false);
  const [hasOpportunities, setHasOpportunities] = useState<boolean>(false);
  const [hideTeamAnalyticsIfBasicUser, setHideTeamAnalyticsIfBasicUser] =
    useState<boolean>(false);
  const [subscriptionBasedCredits, setSubscriptionBasedCredits] =
    useState<boolean>(false);
  const [publicApiAllowed, setPublicApiAllowed] = useState<boolean>(false);
  const [automaticConversion, setAutomaticConversion] =
    useState<boolean>(false);
  const [mergeFreetrials, setMergeFreetrials] = useState<boolean>(false);
  const [hasVoip, setHasVoip] = useState<boolean>(false);
  const [monthVoipMinutes, setMonthVoipMinutes] = useState<number | null>(null);
  const [HasSignals, setHasSignals] = useState<boolean>(false);
  const [HasSequences, setHasSequences] = useState<boolean>(false);
  const [HasTasks, setHasTasks] = useState<boolean>(false);
  const [HasInbox, setHasInbox] = useState<boolean>(false);
  const [HasDashboard, setHasDashboard] = useState<boolean>(false);
  const [HasAnalytics, setHasAnalytics] = useState<boolean>(false);
  const [HasCoreAI, setHasCoreAI] = useState<boolean>(false);
  const [hasLeadScore, setHasLeadScore] = useState<boolean>(false);
  const [maxNumberOfAiVariables, setMaxNumberOfAiVariables] = useState<
    number | null
  >(null);
  const [maxNumberOfTags, setMaxNumberOfTags] = useState<number | null>(null);
  const [maxNumberOfMailBoxes, setMaxNumberOfMailBoxes] = useState<
    number | null
  >(null);
  const [maxNumberOfExternalProperties, setMaxNumberOfExternalProperties] =
    useState<number | null>(null);
  const [hasMCP, setHasMCP] = useState<boolean>(false);
  const [hasWebhooks, setHasWebhooks] = useState<boolean>(false);
  const [hasSlack, setHasSlack] = useState<boolean>(false);
  const [
    stopSequencesWhenMeetingIsCreated,
    setStopSequencesWhenMeetingIsCreated,
  ] = useState<boolean>(false);
  const [stopSequencesWhenPositiveReply, setStopSequencesWhenPositiveReply] =
    useState<boolean>(false);
  const [stopSequencesAtSubscriptionLevel, setStopSequencesAtSubscriptionLevel] =
    useState<boolean>(false);
  const [createTaskTodoInCrm, setCreateTaskTodoInCrm] =
    useState<boolean>(false);
  const [trackSentEmails, setTrackSentEmails] = useState<boolean>(false);
  const [hasReorderProviders, setHasReorderProviders] =
    useState<boolean>(false);
  const [hasAgents, setHasAgents] = useState<boolean>(false);
  const [additionalAiCredits, setAdditionalAiCredits] = useState<number | null>(
    null
  );

  useEffect(() => {
    setCurrency(subscription?.otherSettings?.currency || "");
    setMonthScrapeRequest(
      subscription?.otherSettings?.monthScrapeRequest ?? null
    );
    setMonthVerifyRequest(
      subscription?.otherSettings?.monthVerifyRequest ?? null
    );
    setMonthFindPhoneRequest(
      subscription?.otherSettings?.monthFindPhoneRequest ?? null
    );
    setMonthGenerateTemplateRequest(
      subscription?.otherSettings?.monthGenerateTemplateRequest ?? null
    );
    setHasLastFeatures(subscription?.otherSettings?.hasLastFeatures || false);
    setInputError(null);
    setHasOpportunities(subscription?.otherSettings?.hasOpportunities || false);
    setHideTeamAnalyticsIfBasicUser(
      subscription?.otherSettings?.hideTeamAnalyticsIfBasicUser || false
    );
    setSubscriptionBasedCredits(
      subscription?.otherSettings?.subscriptionBasedCredits || false
    );
    setPublicApiAllowed(subscription?.otherSettings?.publicApiAllowed || false);
    setAutomaticConversion(
      subscription?.otherSettings?.automaticConversion || false
    );
    setMergeFreetrials(subscription?.otherSettings?.mergeFreetrials || false);
    setHasVoip(subscription?.otherSettings?.hasVoip || false);
    setMonthVoipMinutes(
      subscription?.otherSettings?.monthVoipMinutes ?? null
    );
    setHasSignals(subscription?.otherSettings?.hasSignals || false);
    setHasSequences(subscription?.otherSettings?.hasSequences || false);
    setHasTasks(subscription?.otherSettings?.hasTasks || false);
    setHasInbox(subscription?.otherSettings?.hasInbox || false);
    setHasDashboard(subscription?.otherSettings?.hasDashboard || false);
    setHasAnalytics(subscription?.otherSettings?.hasAnalytics || false);
    setHasCoreAI(subscription?.otherSettings?.hasCoreAI || false);
    setHasLeadScore(subscription?.otherSettings?.hasLeadScore || false);
    setMaxNumberOfAiVariables(
      subscription?.otherSettings?.maxNumberOfAiVariables ?? null
    );
    setMaxNumberOfTags(
      subscription?.otherSettings?.maxNumberOfTags ?? null
    );
    setMaxNumberOfMailBoxes(
      subscription?.otherSettings?.maxNumberOfMailBoxes ?? null
    );
    setMaxNumberOfExternalProperties(
      subscription?.otherSettings?.maxNumberOfExternalProperties ?? null
    );
    setHasMCP(subscription?.otherSettings?.hasMCP || false);
    setHasWebhooks(subscription?.otherSettings?.hasWebhooks || false);
    setHasSlack(subscription?.otherSettings?.hasSlack || false);
    setStopSequencesWhenMeetingIsCreated(
      subscription?.otherSettings?.stopSequencesWhenMeetingIsCreated || false
    );
    setStopSequencesWhenPositiveReply(
      subscription?.otherSettings?.stopSequencesWhenPositiveReply || false
    );
    setStopSequencesAtSubscriptionLevel(
      subscription?.otherSettings?.stopSequencesAtSubscriptionLevel || false
    );
    setCreateTaskTodoInCrm(
      subscription?.otherSettings?.createTaskTodoInCrm || false
    );
    setTrackSentEmails(subscription?.otherSettings?.trackSentEmails || false);
    setHasReorderProviders(
      subscription?.otherSettings?.hasReorderProviders || false
    );
    setHasAgents(subscription?.otherSettings?.hasAgents || false);
    setAdditionalAiCredits(
      subscription?.otherSettings?.additionalAiCredits ?? null
    );
  }, [subscription]);

  const {
    mutate: editOtherSettings,
    data: isEditSuccess,
    error: isEditError,
  } = useEditOtherSettings();

  useConditionalSnackBar([
    {
      condition: !!isEditError,
      message: "Error while update other settings",
      severity: "error",
    },
    {
      condition: !!inputError,
      message: inputError!,
      severity: "error",
    },
    {
      condition: !!isEditSuccess,
      message: "Other settings updated successfully",
      severity: "success",
    },
  ]);

  if (!subscription) {
    return (
      <FlexDiv>
        <span> Please select a subscription</span>
      </FlexDiv>
    );
  }

  if (isLoadingSubscription) {
    return (
      <FlexDiv>
        <CircularProgress />
      </FlexDiv>
    );
  }

  const updateOtherSettings = (
    inputs: Omit<OtherSettingsInputs, "subscriptionId">
  ) => {
    editOtherSettings({ ...inputs, subscriptionId: subscription.id });
  };

  return (
    <OtherSettingsWrapper>
      <div className="card-content">
        <div className="account-info-row">
          <p>Currency</p>
          <Select
            className="account-info-select"
            id="currency"
            name="currency"
            value={currency}
            disabled={admin?.canModify ? false : true}
            onChange={(e) => {
              setCurrency(e.target.value as string);
              updateOtherSettings({
                currency: e.target.value as string,
              });
            }}
          >
            <MenuItem key="$" value={"$"}>
              $
            </MenuItem>
            <MenuItem key="€" value={"€"}>
              €
            </MenuItem>
          </Select>
        </div>
        <div className="account-info-row">
          <p>Month Email Credits (per user)</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="monthScrapeRequest"
              name="monthScrapeRequest"
              value={monthScrapeRequest ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                const numberValue = Number(value);

                setMonthScrapeRequest(numberValue);
              }}
              onBlur={() => {
                if (
                  monthScrapeRequest !== null &&
                  monthScrapeRequest !==
                    (subscription.otherSettings?.monthScrapeRequest ?? null)
                ) {
                  updateOtherSettings({
                    monthScrapeRequest: monthScrapeRequest,
                  });
                }
              }}
            />
            {monthScrapeRequest != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMonthScrapeRequest(null);
                      updateOtherSettings({ resetMonthScrapeRequest: true });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Month Verify Credits (per user)</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="monthVerifyRequest"
              name="monthVerifyRequest"
              value={monthVerifyRequest ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                const numberValue = Number(value);

                setMonthVerifyRequest(numberValue);
              }}
              onBlur={() => {
                if (
                  monthVerifyRequest !== null &&
                  monthVerifyRequest !==
                    (subscription.otherSettings?.monthVerifyRequest ?? null)
                ) {
                  updateOtherSettings({
                    monthVerifyRequest: monthVerifyRequest,
                  });
                }
              }}
            />
            {monthVerifyRequest != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMonthVerifyRequest(null);
                      updateOtherSettings({ resetMonthVerifyRequest: true });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Month Phone Credits (per user)</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="monthFindPhoneRequest"
              name="monthFindPhoneRequest"
              value={monthFindPhoneRequest ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                const numberValue = Number(value);

                setMonthFindPhoneRequest(numberValue);
              }}
              onBlur={() => {
                if (
                  monthFindPhoneRequest !== null &&
                  monthFindPhoneRequest !==
                    (subscription.otherSettings?.monthFindPhoneRequest ?? null)
                ) {
                  updateOtherSettings({
                    monthFindPhoneRequest: monthFindPhoneRequest,
                  });
                }
              }}
            />
            {monthFindPhoneRequest != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMonthFindPhoneRequest(null);
                      updateOtherSettings({ resetMonthFindPhoneRequest: true });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p> Last Features</p>
          <div>
            <ActiveSwitch
              id="hasLastFeatures"
              name="hasLastFeatures"
              disabled={admin?.canModify ? false : true}
              checked={hasLastFeatures}
              onChange={(e) => {
                setHasLastFeatures(e.target.checked);
                updateOtherSettings({
                  hasLastFeatures: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Month AI Credits (per user)</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="monthGenerateTemplateRequest"
              name="monthGenerateTemplateRequest"
              value={monthGenerateTemplateRequest ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                const numberValue = Number(value);

                setMonthGenerateTemplateRequest(numberValue);
              }}
              onBlur={() => {
                if (
                  monthGenerateTemplateRequest !== null &&
                  monthGenerateTemplateRequest !==
                    (subscription.otherSettings?.monthGenerateTemplateRequest ??
                      null)
                ) {
                  updateOtherSettings({
                    monthGenerateTemplateRequest: monthGenerateTemplateRequest,
                  });
                }
              }}
            />
            {monthGenerateTemplateRequest != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMonthGenerateTemplateRequest(null);
                      updateOtherSettings({
                        resetMonthGenerateTemplateRequest: true,
                      });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Has Opportunities</p>
          <div>
            <ActiveSwitch
              id="hasOpportunities"
              name="hasOpportunities"
              checked={hasOpportunities}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasOpportunities(e.target.checked);
                updateOtherSettings({
                  hasOpportunities: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Hide Team analytics if Basic User</p>
          <div>
            <ActiveSwitch
              id="hideTeamAnalyticsIfBasicUser"
              name="hideTeamAnalyticsIfBasicUser"
              checked={hideTeamAnalyticsIfBasicUser}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHideTeamAnalyticsIfBasicUser(e.target.checked);
                updateOtherSettings({
                  hideTeamAnalyticsIfBasicUser: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Subscription Based Credits</p>
          <div>
            <ActiveSwitch
              id="subscriptionBasedCredits"
              name="subscriptionBasedCredits"
              checked={subscriptionBasedCredits}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setSubscriptionBasedCredits(e.target.checked);
                updateOtherSettings({
                  subscriptionBasedCredits: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>PublicApiAllowed</p>
          <div>
            <ActiveSwitch
              id="publicApiAllowed"
              name="publicApiAllowed"
              checked={publicApiAllowed}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setPublicApiAllowed(e.target.checked);
                updateOtherSettings({
                  publicApiAllowed: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Automatic Conversion</p>
          <div>
            <ActiveSwitch
              id="automaticConversion"
              name="automaticConversion"
              checked={automaticConversion}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setAutomaticConversion(e.target.checked);
                updateOtherSettings({
                  automaticConversion: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Merge Free Trials</p>
          <div>
            <ActiveSwitch
              id="mergeFreetrials"
              name="mergeFreetrials"
              checked={mergeFreetrials}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setMergeFreetrials(e.target.checked);
                updateOtherSettings({
                  mergeFreetrials: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>VOIP</p>
          <div>
            <ActiveSwitch
              id="hasVoip"
              name="hasVoip"
              checked={hasVoip}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasVoip(e.target.checked);
                updateOtherSettings({
                  hasVoip: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        {hasVoip && (
          <div className="account-info-row">
            <p>Month Voip Minutes (per user)</p>
            <div className="field-with-reset">
              <ChipInput
                className="add-new-field"
                id="monthVoipMinutes"
                name="monthVoipMinutes"
                value={monthVoipMinutes ?? ""}
                disabled={admin?.canModify ? false : true}
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (isNaN(Number(value))) {
                    setInputError("Please enter a number");
                    return;
                  }
                  const numberValue = Number(value);
                  setMonthVoipMinutes(numberValue);
                }}
                onBlur={() => {
                  if (
                    monthVoipMinutes !== null &&
                    monthVoipMinutes !==
                      (subscription.otherSettings?.monthVoipMinutes ?? null)
                  ) {
                    updateOtherSettings({
                      monthVoipMinutes: monthVoipMinutes,
                    });
                  }
                }}
              />
              {monthVoipMinutes != null && (
                <Tooltip title="Reset">
                  <span>
                    <IconButton
                      className="reset-button"
                      size="small"
                      disabled={admin?.canModify ? false : true}
                      onClick={() => {
                        setMonthVoipMinutes(null);
                        updateOtherSettings({ resetMonthVoipMinutes: true });
                      }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}
        <div className="account-info-row">
          <p>Signals</p>
          <div>
            <ActiveSwitch
              id="hasSignals"
              name="hasSignals"
              checked={HasSignals}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasSignals(e.target.checked);
                updateOtherSettings({
                  hasSignals: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Sequences</p>
          <div>
            <ActiveSwitch
              id="hasSequences"
              name="hasSequences"
              checked={HasSequences}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasSequences(e.target.checked);
                updateOtherSettings({
                  hasSequences: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Tasks</p>
          <div>
            <ActiveSwitch
              id="hasTasks"
              name="hasTasks"
              checked={HasTasks}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasTasks(e.target.checked);
                updateOtherSettings({
                  hasTasks: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Inbox</p>
          <div>
            <ActiveSwitch
              id="hasInbox"
              name="hasInbox"
              checked={HasInbox}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasInbox(e.target.checked);
                updateOtherSettings({
                  hasInbox: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Dashboard</p>
          <div>
            <ActiveSwitch
              id="hasDashboard"
              name="hasDashboard"
              checked={HasDashboard}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasDashboard(e.target.checked);
                updateOtherSettings({
                  hasDashboard: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Analytics</p>
          <div>
            <ActiveSwitch
              id="hasAnalytics"
              name="hasAnalytics"
              checked={HasAnalytics}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasAnalytics(e.target.checked);
                updateOtherSettings({
                  hasAnalytics: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Core AI</p>
          <div>
            <ActiveSwitch
              id="hasCoreAI"
              name="hasCoreAI"
              checked={HasCoreAI}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasCoreAI(e.target.checked);
                updateOtherSettings({
                  hasCoreAI: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Lead Score</p>
          <div>
            <ActiveSwitch
              id="hasLeadScore"
              name="hasLeadScore"
              checked={hasLeadScore}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasLeadScore(e.target.checked);
                updateOtherSettings({
                  hasLeadScore: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>MCP</p>
          <div>
            <ActiveSwitch
              id="hasMCP"
              name="hasMCP"
              checked={hasMCP}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasMCP(e.target.checked);
                updateOtherSettings({
                  hasMCP: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Webhooks</p>
          <div>
            <ActiveSwitch
              id="hasWebhooks"
              name="hasWebhooks"
              checked={hasWebhooks}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasWebhooks(e.target.checked);
                updateOtherSettings({
                  hasWebhooks: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Slack</p>
          <div>
            <ActiveSwitch
              id="hasSlack"
              name="hasSlack"
              checked={hasSlack}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasSlack(e.target.checked);
                updateOtherSettings({
                  hasSlack: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Stop Sequences When Meeting Is Created</p>
          <div>
            <ActiveSwitch
              id="stopSequencesWhenMeetingIsCreated"
              name="stopSequencesWhenMeetingIsCreated"
              checked={stopSequencesWhenMeetingIsCreated}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setStopSequencesWhenMeetingIsCreated(e.target.checked);
                updateOtherSettings({
                  stopSequencesWhenMeetingIsCreated: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Stop Sequences When Positive Reply</p>
          <div>
            <ActiveSwitch
              id="stopSequencesWhenPositiveReply"
              name="stopSequencesWhenPositiveReply"
              checked={stopSequencesWhenPositiveReply}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setStopSequencesWhenPositiveReply(e.target.checked);
                updateOtherSettings({
                  stopSequencesWhenPositiveReply: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Stop Sequences At Subscription Level</p>
          <div>
            <ActiveSwitch
              id="stopSequencesAtSubscriptionLevel"
              name="stopSequencesAtSubscriptionLevel"
              checked={stopSequencesAtSubscriptionLevel}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setStopSequencesAtSubscriptionLevel(e.target.checked);
                updateOtherSettings({
                  stopSequencesAtSubscriptionLevel: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Create Single Tasks In CRM</p>
          <div>
            <ActiveSwitch
              id="createTaskTodoInCrm"
              name="createTaskTodoInCrm"
              checked={createTaskTodoInCrm}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setCreateTaskTodoInCrm(e.target.checked);
                updateOtherSettings({
                  createTaskTodoInCrm: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Track Emails Sent Outside Crono</p>
          <div>
            <ActiveSwitch
              id="trackSentEmails"
              name="trackSentEmails"
              checked={trackSentEmails}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setTrackSentEmails(e.target.checked);
                updateOtherSettings({
                  trackSentEmails: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Max Number Of AI Variables</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="maxNumberOfAiVariables"
              name="maxNumberOfAiVariables"
              value={maxNumberOfAiVariables ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (value === "") {
                  setMaxNumberOfAiVariables(null);
                  return;
                }
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                setMaxNumberOfAiVariables(Number(value));
              }}
              onBlur={() => {
                if (
                  maxNumberOfAiVariables !==
                  (subscription.otherSettings?.maxNumberOfAiVariables ?? null)
                ) {
                  updateOtherSettings({
                    maxNumberOfAiVariables: maxNumberOfAiVariables,
                  });
                }
              }}
            />
            {maxNumberOfAiVariables != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMaxNumberOfAiVariables(null);
                      updateOtherSettings({
                        resetMaxNumberOfAiVariables: true,
                      });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Max Number Of Tags</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="maxNumberOfTags"
              name="maxNumberOfTags"
              value={maxNumberOfTags ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (value === "") {
                  setMaxNumberOfTags(null);
                  return;
                }
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                setMaxNumberOfTags(Number(value));
              }}
              onBlur={() => {
                if (
                  maxNumberOfTags !==
                  (subscription.otherSettings?.maxNumberOfTags ?? null)
                ) {
                  updateOtherSettings({
                    maxNumberOfTags: maxNumberOfTags,
                  });
                }
              }}
            />
            {maxNumberOfTags != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMaxNumberOfTags(null);
                      updateOtherSettings({ resetMaxNumberOfTags: true });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Max Number Of Mailboxes</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="maxNumberOfMailBoxes"
              name="maxNumberOfMailBoxes"
              value={maxNumberOfMailBoxes ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (value === "") {
                  setMaxNumberOfMailBoxes(null);
                  return;
                }
                if (isNaN(Number(value)) || Number(value) < 0) {
                  setInputError("Please enter a non-negative number");
                  return;
                }
                setMaxNumberOfMailBoxes(Number(value));
              }}
              onBlur={() => {
                if (
                  maxNumberOfMailBoxes !== null &&
                  maxNumberOfMailBoxes !==
                    (subscription.otherSettings?.maxNumberOfMailBoxes ?? null)
                ) {
                  updateOtherSettings({
                    maxNumberOfMailBoxes: maxNumberOfMailBoxes,
                  });
                }
              }}
            />
            {maxNumberOfMailBoxes != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMaxNumberOfMailBoxes(null);
                      updateOtherSettings({ resetMaxNumberOfMailBoxes: true });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Reorder Providers</p>
          <div>
            <ActiveSwitch
              id="hasReorderProviders"
              name="hasReorderProviders"
              checked={hasReorderProviders}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasReorderProviders(e.target.checked);
                updateOtherSettings({
                  hasReorderProviders: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Agents</p>
          <div>
            <ActiveSwitch
              id="hasAgents"
              name="hasAgents"
              checked={hasAgents}
              disabled={admin?.canModify ? false : true}
              onChange={(e) => {
                setHasAgents(e.target.checked);
                updateOtherSettings({
                  hasAgents: e.target.checked,
                });
              }}
            />
          </div>
        </div>
        <div className="account-info-row">
          <p>Additional AI Credits</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="additionalAiCredits"
              name="additionalAiCredits"
              value={additionalAiCredits ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (value === "") {
                  setAdditionalAiCredits(null);
                  return;
                }
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                setAdditionalAiCredits(Number(value));
              }}
              onBlur={() => {
                if (
                  additionalAiCredits !==
                  (subscription.otherSettings?.additionalAiCredits ?? null)
                ) {
                  updateOtherSettings({
                    additionalAiCredits: additionalAiCredits,
                  });
                }
              }}
            />
            {additionalAiCredits != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setAdditionalAiCredits(null);
                      updateOtherSettings({
                        resetAdditionalAiCredits: true,
                      });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="account-info-row">
          <p>Max Number Of External Properties</p>
          <div className="field-with-reset">
            <ChipInput
              className="add-new-field"
              id="maxNumberOfExternalProperties"
              name="maxNumberOfExternalProperties"
              value={maxNumberOfExternalProperties ?? ""}
              disabled={admin?.canModify ? false : true}
              onChange={(e: any) => {
                const value = e.target.value;
                if (value === "") {
                  setMaxNumberOfExternalProperties(null);
                  return;
                }
                if (isNaN(Number(value))) {
                  setInputError("Please enter a number");
                  return;
                }
                setMaxNumberOfExternalProperties(Number(value));
              }}
              onBlur={() => {
                if (
                  maxNumberOfExternalProperties !==
                  (subscription.otherSettings?.maxNumberOfExternalProperties ??
                    null)
                ) {
                  updateOtherSettings({
                    maxNumberOfExternalProperties:
                      maxNumberOfExternalProperties,
                  });
                }
              }}
            />
            {maxNumberOfExternalProperties != null && (
              <Tooltip title="Reset">
                <span>
                  <IconButton
                    className="reset-button"
                    size="small"
                    disabled={admin?.canModify ? false : true}
                    onClick={() => {
                      setMaxNumberOfExternalProperties(null);
                      updateOtherSettings({
                        resetMaxNumberOfExternalProperties: true,
                      });
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </OtherSettingsWrapper>
  );
};

export default OtherSettingsInfo;
